import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { signalRService, SpinCompletedEvent, SpinStartedEvent, MatchResult } from '../services/signalRService.ts';
import { wheelApi, Wheel as WheelType, User, Slot as SlotType } from '../services/api.ts';
import { RegistrationDialog } from './RegistrationDialog.tsx';
import { MatchesDialog } from './MatchesDialog/index.tsx';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChair } from '@fortawesome/free-solid-svg-icons';
import { faMars } from '@fortawesome/free-solid-svg-icons';
import { faVenus } from '@fortawesome/free-solid-svg-icons';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';
import styles from '../styles/Wheel.module.css'; // Revert to original import
import { VideoCall } from './VideoCall/index.tsx';
import { webRTCService } from '../services/webRTCService.ts';

const ValueProp = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 0 1rem;
`;

const Heading = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  margin-bottom: 0.75rem;
  background: linear-gradient(90deg, #ff6b6b 0%, #ffd93d 50%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;

  span {
    margin: 0;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    gap: 0.3rem;
  }
`;

const SubHeading = styled.p`
  font-size: clamp(0.875rem, 2vw, 1rem);
  color: #A0AEC0;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.5;
  letter-spacing: 0.02em;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 0.875rem;
    max-width: 100%;
  }
`;

interface WheelProps {
  wheelData?: WheelType;
}

export const Wheel: React.FC<WheelProps> = ({ wheelData: initialWheelData }) => {
  const [wheel, setWheel] = useState<WheelType | null>(initialWheelData || null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SlotType | null>(null);
  const [timeToNextSpin, setTimeToNextSpin] = useState<number | null>(null);
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const [showMatchesDialog, setShowMatchesDialog] = useState(false);
  const [currentMatches, setCurrentMatches] = useState<MatchResult[]>([]);
  const [wheelSize, setWheelSize] = useState(800);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [activeMatchId, setActiveMatchId] = useState<string | null>(null);
  const innerWheelRef = useRef<HTMLDivElement>(null);
  const outerWheelRef = useRef<HTMLDivElement>(null);
  const isConnectedRef = useRef(false);

  // Flag to determine if this is a private wheel (passed via props)
  const isPrivateWheel = !!initialWheelData;

  useEffect(() => {
    const updateWheelSize = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const minDimension = Math.min(viewportWidth, viewportHeight);
      const maxWheelSize = Math.min(800, minDimension * 0.9);
      setWheelSize(maxWheelSize);
    };

    updateWheelSize();
    window.addEventListener('resize', updateWheelSize);
    return () => window.removeEventListener('resize', updateWheelSize);
  }, []);

  const updateTimeToNextSpin = useCallback((lastSpinDate: string) => {
    const lastSpin = new Date(lastSpinDate);
    const nextSpin = new Date(lastSpin.getTime() + 60 * 1000); // 60 seconds
    const now = new Date();
    const timeLeft = nextSpin.getTime() - now.getTime();
    setTimeToNextSpin(Math.max(0, timeLeft));
  }, []);

  useEffect(() => {
    const initializeWheel = async () => {
      try {
        // First connect to SignalR if not already connected
        if (!isConnectedRef.current) {
          await signalRService.connect();
          isConnectedRef.current = true;
        }
        
        // Only fetch wheel data if not provided via props
        if (!initialWheelData) {
          const publicWheelData = await wheelApi.getPublicWheel();
          setWheel(publicWheelData);
          
          // Wait for connection to be fully established before joining wheel group
          if (signalRService.getConnectionId()) {
            await signalRService.joinWheel(publicWheelData.id);
          } else {
            console.warn('SignalR connection not fully established, retrying in 1 second...');
            setTimeout(async () => {
              if (signalRService.getConnectionId()) {
                await signalRService.joinWheel(publicWheelData.id);
              } else {
                console.error('Failed to establish SignalR connection');
              }
            }, 1000);
          }

          // Check for existing user
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
          if (storedUser) {
              console.log('User found:', storedUser);
              setCurrentUser(storedUser);
          }

          if (publicWheelData.lastSpinDate) {
            updateTimeToNextSpin(publicWheelData.lastSpinDate);
          }
        } else {
          // Use the provided wheel data
          // Wait for connection to be fully established before joining wheel group
          if (signalRService.getConnectionId()) {
            await signalRService.joinWheel(initialWheelData.id);
          } else {
            console.warn('SignalR connection not fully established, retrying in 1 second...');
            setTimeout(async () => {
              if (signalRService.getConnectionId()) {
                await signalRService.joinWheel(initialWheelData.id);
              } else {
                console.error('Failed to establish SignalR connection');
              }
            }, 1000);
          }
          
          // Check for existing user with the provided wheel data
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
          if (storedUser) {
            console.log('User found:', storedUser);
            setCurrentUser(storedUser);
          }

          if (initialWheelData.lastSpinDate) {
            updateTimeToNextSpin(initialWheelData.lastSpinDate);
          }
        }
      } catch (error) {
        console.error('Error initializing wheel:', error);
      }
    };

    initializeWheel();

    return () => {
      if (wheel && isConnectedRef.current) {
        signalRService.leaveWheel(wheel.id);
        signalRService.disconnect();
        isConnectedRef.current = false;
      }
    };
  }, [initialWheelData]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (timeToNextSpin !== null && timeToNextSpin > 0) {
        setTimeToNextSpin(prev => prev !== null ? prev - 1000 : null);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeToNextSpin]);

  const spinWheel = (wheelRef: HTMLDivElement, finalAngle: number) => {
    // Initial fast spin
    wheelRef.style.transform = 'rotate(0deg)';
    wheelRef.classList.add(styles.spinningStart);
    
    requestAnimationFrame(() => {
      wheelRef.style.transform = 'rotate(1080deg)'; // 3 full rotations
      
      // After initial spin, transition to final position
      setTimeout(() => {
        wheelRef.classList.remove(styles.spinningStart);
        wheelRef.classList.add(styles.spinningEnd);
        wheelRef.style.transform = `rotate(${finalAngle}deg)`;
        
        // Add bounce effect at the end
        setTimeout(() => {
          wheelRef.classList.remove(styles.spinningEnd);
          wheelRef.style.setProperty('--final-rotation', `${finalAngle}deg`);
          wheelRef.classList.add(styles.spinComplete);
        }, 2000);
      }, 2000);
    });
  };

  useEffect(() => {
    const handleSpinStart = (event: CustomEvent<SpinStartedEvent>) => {
      if (!wheel || !innerWheelRef.current || !outerWheelRef.current) return;
      setIsSpinning(true);

      const slotsPerRing = wheel.slotCount / 2;
      const baseRotations = 5;
      const innerAngle = (event.detail.innerOffset * (360 / slotsPerRing)) + (baseRotations * 360);
      const outerAngle = (event.detail.outerOffset * (360 / slotsPerRing)) + (baseRotations * 360);

      spinWheel(innerWheelRef.current, innerAngle);
      spinWheel(outerWheelRef.current, outerAngle);
    };

    const handleSpinComplete = (event: CustomEvent<SpinCompletedEvent>) => {
      if (!wheel || event.detail.wheelId !== wheel.id) return;

      // Show matches only if there are any matches and the current user is involved
      if (event.detail.matches.length > 0 && currentUser) {
        const userMatches = event.detail.matches.filter(match => 
          match.user1.id === currentUser.id || match.user2.id === currentUser.id
        );
        
        if (userMatches.length > 0) {
          setCurrentMatches(userMatches);
          setShowMatchesDialog(true);
        }
      }

      // Wait for 8 seconds before resetting slots
      setTimeout(() => {
        setIsSpinning(false);
        if (innerWheelRef.current) {
          innerWheelRef.current.className = styles.innerWheel;
          innerWheelRef.current.style.transform = 'rotate(0deg)';
        }
        if (outerWheelRef.current) {
          outerWheelRef.current.className = styles.outerWheel;
          outerWheelRef.current.style.transform = 'rotate(0deg)';
        }

        // Reset wheel state
        setWheel(prev => prev ? {
          ...prev,
          currentRound: event.detail.currentRound,
          lastSpinDate: event.detail.lastSpinDate,
          slots: prev.slots.map(slot => ({ ...slot, userId: undefined }))
        } : null);

        updateTimeToNextSpin(event.detail.lastSpinDate);
      }, 8000);
    };

    document.addEventListener('wheelSpinStarted', handleSpinStart as EventListener);
    document.addEventListener('wheelSpinCompleted', handleSpinComplete as EventListener);

    return () => {
      document.removeEventListener('wheelSpinStarted', handleSpinStart as EventListener);
      document.removeEventListener('wheelSpinCompleted', handleSpinComplete as EventListener);
    };
  }, [wheel, currentUser, updateTimeToNextSpin]);

  const handleSlotClick = async (slot: SlotType) => {
    if (slot.userId || isSpinning || !wheel) return;
    
    // Check if user is already in a slot
    if (currentUser) {
      const userSlot = wheel.slots.find(s => s.userId === currentUser.id);
      if (userSlot) {
        alert('You are already in a slot!');
        return;
      }
      
      // User is registered but not in a slot, take the slot directly
      try {
        await signalRService.takeSlot(wheel.id, currentUser.id, slot.id);
      } catch (error) {
        console.error('Error taking slot:', error);
      }
      return;
    }
    
    // User is not registered, show registration dialog
    setSelectedSlot(slot);
    setShowRegistrationDialog(true);
  };

  const handleRegister = async (formData: Record<string, any>) => {
    try {
      // Generate a unique session ID if one doesn't exist
      const sessionId = uuidv4();
      
      // Create registration data with required fields
      const registrationData = {
        sessionId: sessionId,
        age: formData.age,
        gender: formData.gender,
        shortBio: formData.bio,
        whoAmI: formData.whoAmI,
        interests: formData.interests,
        username: formData.username,
        // Add connection ID if available
        connectionId: signalRService.getConnectionId()
      };
      
      // Register user
      const user = await wheelApi.registerUser(registrationData);
      setCurrentUser(user);
      // Store the complete user object
      localStorage.setItem('user', JSON.stringify(user));

      // Update wheel state with new user before taking slot
      setWheel(prev => {
        if (!prev) return null;
        return {
          ...prev,
          activeUsers: prev.activeUsers ? [...prev.activeUsers, user] : [user]
        };
      });

      setShowRegistrationDialog(false);
      
      // If a slot was selected, take it now that we're registered
      if (selectedSlot && wheel) { // Add explicit null check for wheel
        await signalRService.takeSlot(wheel.id, user.id, selectedSlot.id);
        setSelectedSlot(null);
      }
    } catch (error) {
      console.error('Error registering user:', error);
    }
  };

  const calculateSlotSize = (totalSlots: number) => {
    if (!wheel) return 0; // Add this null check
    const wheelDiameter = wheelSize;
    const circumference = Math.PI * wheelDiameter;
    const slotsPerRing = totalSlots / 2;
    
    // Calculate size to fit slots with some spacing
    const maxSlotSize = Math.min(wheelSize * 0.125, 100); // Max 12.5% of wheel size or 100px
    const slotSize = Math.min(maxSlotSize, (circumference / slotsPerRing) * 0.4);
    return slotSize;
  };

  const getSlotStyle = (position: number, isOuter: boolean) => {
    if (!wheel) return {};
    
    const totalSlots = wheel.slotCount;
    const slotsPerRing = totalSlots / 2;
    const slotSize = calculateSlotSize(totalSlots);
    
    // Adjust radius based on wheel and slot size
    const wheelRadius = wheelSize / 2;
    const outerRadius = wheelRadius - slotSize;
    const innerRadius = wheelRadius * 0.75 - slotSize;
    const radius = isOuter ? outerRadius : innerRadius;
    const angle = (position * (360 / slotsPerRing)) * (Math.PI / 180);
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    
    return {
      transform: `translate(${x}px, ${y}px)`,
      width: `${slotSize}px`,
      height: `${slotSize}px`,
      fontSize: `${slotSize * 0.3}px`
    };
  };

  const getWheelStyle = (isInner: boolean) => {
    const size = isInner ? wheelSize * 0.8 : wheelSize;
    const offset = isInner ? (wheelSize - size) / 2 : 0;
    
    return {
      width: `${size}px`,
      height: `${size}px`,
      ...(isInner && {
        top: `${offset}px`,
        left: `${offset}px`
      })
    };
  };

  const renderSlotContent = (slot: SlotType) => {
    if (!slot.userId || !wheel) {
      return <FontAwesomeIcon icon={faChair} className={styles.slotIcon} />;
    }

    // Find user in activeUsers array
    const user = wheel.activeUsers?.find(u => u.id === slot.userId);
    //console.log('Found user:', user);

    if (user) {
      const userGender = user.gender || 'other';
      const isUserMale = userGender.toLowerCase() === 'male';
      //console.log('User gender:', user.gender, 'Is male:', isUserMale);
      
      return (
        <FontAwesomeIcon 
          icon={userGender == "other" ? faCircleUser : (isUserMale ? faMars : faVenus)}
          className={`${styles.slotIcon} ${userGender == "other" ? "" : styles[userGender.toLowerCase()]}`}
          style={{ color: userGender == "other" ? '#000' : (isUserMale ? '#007bff' : '#ff3366') }}
        />
      );
    }

    //console.log('No user found, showing chair');
    return <FontAwesomeIcon icon={faChair} className={styles.slotIcon} />;
  };

  const renderSlots = (isOuter: boolean) => {
    if (!wheel) return null;
    
    const totalSlots = wheel.slotCount;
    const slotsPerRing = totalSlots / 2;
    const slots = Array.from({ length: slotsPerRing }, (_, i) => {
      // Calculate the actual position based on whether it's inner or outer ring
      const position = isOuter ? i + slotsPerRing : i;
      const existingSlot = wheel.slots.find(s => s.position === position);
      
      return {
        id: existingSlot?.id ?? position,
        position: position,
        isOuter,
        userId: existingSlot?.userId,
        wheelId: wheel.id,
        occupiedAt: existingSlot?.occupiedAt,
        user: existingSlot?.user
      } as SlotType;
    });

    return slots.map(slot => (
      <div
        key={slot.id}
        className={`${styles.slot} ${slot.userId ? styles.selected : ''}`}
        style={getSlotStyle(slot.position % slotsPerRing, slot.isOuter)}
        onClick={() => handleSlotClick(slot)}
      >
        {renderSlotContent(slot)}
      </div>
    ));
  };

  useEffect(() => {
    const handleSlotTaken = (event: CustomEvent<{ userId: string; slotId: number }>) => {
      console.log('Slot taken event:', event.detail);
      const { userId, slotId } = event.detail;
      
      setWheel(prev => {
        if (!prev) return null;
        
        // First, clear any existing slot this user might be in
        const updatedSlots = prev.slots.map(slot =>
          slot.userId === userId
            ? { ...slot, userId: undefined, occupiedAt: undefined }
            : slot
        );

        // Then, update the target slot with the new user
        const finalSlots = updatedSlots.map(slot =>
          slot.id === slotId
            ? { ...slot, userId, occupiedAt: new Date().toISOString() }
            : slot
        );

        const updatedWheel = {
          ...prev,
          slots: finalSlots
        };
        
        console.log('Updated wheel:', updatedWheel);
        return updatedWheel;
      });
    };

    const handleUserJoined = (event: CustomEvent<{ id: string; age?: number; gender?: string; isRegistered: boolean; username?: string }>) => {
      console.log('User joined event:', event.detail);
      const newUser = event.detail;
      
      setWheel(prev => {
        if (!prev) return null;

        // Only add the user if they're not already in activeUsers
        const existingUserIndex = prev.activeUsers.findIndex(u => u.id === newUser.id);
        const updatedActiveUsers = existingUserIndex >= 0
          ? prev.activeUsers.map((u, i) => i === existingUserIndex ? newUser : u)
          : [...prev.activeUsers, newUser];

        return {
          ...prev,
          activeUsers: updatedActiveUsers
        };
      });
    };

    const handleSlotLeft = (event: CustomEvent<{ userId: string; slotId: number }>) => {
      console.log('Slot left event:', event.detail);
      const { userId, slotId } = event.detail;
      
      setWheel(prev => {
        if (!prev) return null;

        // Update slots by removing the user from their slot, checking both slotId and userId
        const updatedWheel = {
          ...prev,
          slots: prev.slots.map(slot =>
            (slot.id === slotId && slot.userId === userId)
              ? { ...slot, userId: undefined, occupiedAt: undefined }
              : slot
          ),
          // Remove user from activeUsers array
          activeUsers: prev.activeUsers.filter(user => user.id !== userId)
        };

        console.log('Updated wheel after user left:', updatedWheel);
        return updatedWheel;
      });
    };

    document.addEventListener('slotTaken', handleSlotTaken as EventListener);
    document.addEventListener('slotLeft', handleSlotLeft as EventListener);
    document.addEventListener('userJoined', handleUserJoined as EventListener);
    
    return () => {
      document.removeEventListener('slotTaken', handleSlotTaken as EventListener);
      document.removeEventListener('slotLeft', handleSlotLeft as EventListener);
      document.removeEventListener('userJoined', handleUserJoined as EventListener);
    };
  }, []);

  const handleAcceptMatch = async (matchId: string) => {
    try {
      await signalRService.acceptMatch(matchId);
      console.log('Successfully accepted match:', matchId);
      setCurrentMatches(prev => 
        prev.map(match => 
          match.id === matchId 
            ? { ...match, status: 'Accepted' }
            : match
        )
      );
    } catch (error) {
      console.error('Error accepting match:', error);
      alert('Failed to accept match. Please try again.');
    }
  };

  const handleDeclineMatch = async (matchId: string) => {
    try {
      await signalRService.declineMatch(matchId);
      console.log('Successfully declined match:', matchId);
      setCurrentMatches(prev => 
        prev.map(match => 
          match.id === matchId 
            ? { ...match, status: 'Rejected' }
            : match
        )
      );
    } catch (error) {
      console.error('Error declining match:', error);
      alert('Failed to decline match. Please try again.');
    }
  };

  const handleStartVideoCall = (matchId: string) => {
    setActiveMatchId(matchId);
    setShowVideoCall(true);
  };

  useEffect(() => {
    const handleMatchAccepted = (event: CustomEvent<{ matchId: string; userId: string; acceptedByUser1: boolean; acceptedByUser2: boolean }>) => {
      const { matchId, userId, acceptedByUser1, acceptedByUser2 } = event.detail;
      setCurrentMatches(prev => 
        prev.map(match => {
          if (match.id === matchId) {
            return {
              ...match,
              acceptedByUser1,
              acceptedByUser2,
              status: acceptedByUser1 && acceptedByUser2 ? 'Accepted' : 'Pending'
            };
          }
          return match;
        })
      );
      if (currentMatches.find(match => match.id === matchId)?.user1.id === currentUser?.id && acceptedByUser1 && acceptedByUser2) {
        handleStartVideoCall(matchId);
      }
    };

    document.addEventListener('matchAccepted', handleMatchAccepted as EventListener);

    return () => {
      document.removeEventListener('matchAccepted', handleMatchAccepted as EventListener);
    };
  }, []);

  useEffect(() => {
    const handleOfferReceived = (event: CustomEvent<{ matchId: string; offer: RTCSessionDescriptionInit }>) => {
      setActiveMatchId(event.detail.matchId);
      setShowVideoCall(true);
      console.log('Offer received:', event.detail);
      webRTCService.handleOffer(event.detail.matchId, event.detail.offer);
      
    };

    const handleAnswerReceived = (event: CustomEvent<{ matchId: string; answer: RTCSessionDescriptionInit }>) => {
      console.log('Answer received:', event.detail);
      webRTCService.handleAnswer(event.detail.answer);
    };

    const handleIceCandidateReceived = (event: CustomEvent<{ matchId: string; candidate: RTCIceCandidateInit }>) => {
      console.log('ICE candidate received:', event.detail);
      webRTCService.handleIceCandidate(event.detail.candidate);
    };

    // Add event listeners for WebRTC signaling
    document.addEventListener('offerReceived', handleOfferReceived as EventListener);
    document.addEventListener('answerReceived', handleAnswerReceived as EventListener);
    document.addEventListener('iceCandidateReceived', handleIceCandidateReceived as EventListener);

    return () => {
      // Remove event listeners
      document.removeEventListener('offerReceived', handleOfferReceived as EventListener);
      document.removeEventListener('answerReceived', handleAnswerReceived as EventListener);
      document.removeEventListener('iceCandidateReceived', handleIceCandidateReceived as EventListener);
    };
  }, []);

  if (!wheel) return <div>Loading...</div>;

  return (
    <>
       {!isPrivateWheel && (
        <ValueProp>
       
          <Heading>
            Connect Anywhere, Instantly.
          </Heading>
           
          <SubHeading>
            Transform new cities into vibrant communities. Spin to connect with like-minded <br /> people  and forge genuine friendships with ease.
          </SubHeading>
          
        </ValueProp>
      )}
     
      <div className={styles.wheelContainer} style={{ width: `${wheelSize}px`, height: `${wheelSize}px` }}>
        <div 
          ref={outerWheelRef}
          className={styles.outerWheel}
          style={getWheelStyle(false)}
        >
          {renderSlots(true)}
        </div>
        <div 
          ref={innerWheelRef}
          className={styles.innerWheel}
          style={getWheelStyle(true)}
        >
          {renderSlots(false)}
        </div>

        {timeToNextSpin !== null && (
          <div className={styles.timer} style={{ 
            width: `${wheelSize * 0.125}px`, 
            height: `${wheelSize * 0.125}px`,
            fontSize: `${wheelSize * 0.05}px`
          }}>
            {timeToNextSpin > 0 ? `${Math.ceil(timeToNextSpin / 1000)}s` : 'Wait'}
          </div>
        )}

        {showRegistrationDialog && (
          <RegistrationDialog
            onSubmit={handleRegister}
            onClose={() => setShowRegistrationDialog(false)}
            registrationConfig={wheel?.registrationFields ? JSON.parse(wheel.registrationFields) : undefined}
          />
        )}

        {showMatchesDialog && (
          <MatchesDialog
            matches={currentMatches}
            currentUserId={currentUser?.id || ''}
            onAcceptMatch={handleAcceptMatch}
            onDeclineMatch={handleDeclineMatch}
            onClose={() => setShowMatchesDialog(false)}
          />
        )}

        {showVideoCall && activeMatchId && (
          <VideoCall
            matchId={activeMatchId}
            isUser1={currentMatches.find(match => match.id === activeMatchId)?.user1.id === currentUser?.id}
            onClose={() => {
              setShowVideoCall(false);
              setActiveMatchId(null);
            }}
          />
        )}
      </div>
    </>
  );
}; 